import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGame } from '@/context/GameContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, HelpCircle, Play, Shield, Settings, Globe } from 'lucide-react';
import { playClick } from '@/utils/sounds';
import RadioTower from '@/components/RadioTower';
import { 
  RetroBackground, 
  RetroPanel, 
  RetroCard,
  RetroButton,
  RetroText,
  ScoreboardDisplay,
  bandedGradient,
  PALETTE,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const { resetGame } = useGame();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await axios.get(`${API}/games`, {
          headers: getAuthHeader()
        });
        setGames(response.data);
      } catch (error) {
        console.error('Failed to fetch games:', error);
      } finally {
        setLoading(false);
      }
    };

    const checkAdmin = async () => {
      try {
        const response = await axios.get(`${API}/admin/check`, {
          headers: getAuthHeader()
        });
        setIsAdmin(response.data.is_admin);
      } catch (error) {
        // Not admin, that's fine
      }
    };

    fetchGames();
    checkAdmin();
  }, [getAuthHeader]);

  const handleNewGame = () => {
    playClick();
    // Check if user has read How to Play
    const hasReadHowToPlay = localStorage.getItem('hasReadHowToPlay');
    if (!hasReadHowToPlay) {
      toast.info('Please read the rules first!');
      navigate('/how-to-play');
      return;
    }
    resetGame();
    navigate('/new-game');
  };

  const handleDeleteGame = async (gameId) => {
    playClick();
    try {
      await axios.delete(`${API}/games/${gameId}`, {
        headers: getAuthHeader()
      });
      setGames(games.filter(g => g.id !== gameId));
      toast.success('Game deleted');
    } catch (error) {
      toast.error('Failed to delete game');
    }
  };

  const handleLogout = () => {
    playClick();
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <RetroBackground variant="cream" className="p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center gap-4">
            <RadioTower size={50} animated={true} />
            <div>
              <h1 
                style={{
                  fontFamily: DESIGN_TOKENS.fonts.display,
                  fontSize: DESIGN_TOKENS.fontSizes.displayMd,
                  color: PALETTE.ink,
                  textShadow: `
                    2px 2px 0px ${PALETTE.cream},
                    3px 3px 0px ${PALETTE.dirt}
                  `,
                  letterSpacing: '2px',
                }}
              >
                RADIO BASEBALL
              </h1>
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontWeight: 600,
                fontSize: DESIGN_TOKENS.fontSizes.uiSm,
                color: PALETTE.grass,
                marginTop: '4px',
              }}>
                Welcome, {user?.username}!
              </p>
            </div>
          </div>
          <RetroButton
            variant="danger"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            LOGOUT
          </RetroButton>
        </div>

        {/* Game Mode Selection */}
        <RetroPanel title="START A GAME" className="mb-6">
          {/* Default Mode - Apple Music */}
          <button
            onClick={handleNewGame}
            className="w-full mb-4 flex items-center justify-between transition-transform hover:translate-y-0.5 active:translate-y-1"
            style={{
              padding: '16px 20px',
              background: bandedGradient('#5CA34A', PALETTE.grass, '#3d7330'),
              border: `${DESIGN_TOKENS.borders.thick} solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.lg,
              boxShadow: `
                inset 2px 2px 0px rgba(255,255,255,0.2),
                inset -2px -2px 0px rgba(0,0,0,0.3),
                ${DESIGN_TOKENS.shadows.button}
              `,
            }}
            data-testid="new-game-btn"
          >
            <div className="flex items-center gap-4">
              <Play size={28} color={PALETTE.cream} />
              <div className="text-left">
                <span 
                  className="block"
                  style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontWeight: 700,
                    fontSize: '17px',
                    color: PALETTE.cream,
                    letterSpacing: '1px',
                  }}
                >
                  NEW GAME
                </span>
                <span 
                  style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '12px',
                    color: `${PALETTE.cream}cc`,
                  }}
                >
                  30-second song previews
                </span>
              </div>
            </div>
          </button>
          
          {/* Play Online */}
          <button
            onClick={() => { playClick(); navigate('/online'); }}
            className="w-full mb-4 flex items-center justify-between transition-transform hover:translate-y-0.5 active:translate-y-1"
            style={{
              padding: '16px 20px',
              background: bandedGradient('#4BA3FF', '#1E90FF', '#155FA8'),
              border: `${DESIGN_TOKENS.borders.thick} solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.lg,
              boxShadow: `
                inset 2px 2px 0px rgba(255,255,255,0.2),
                inset -2px -2px 0px rgba(0,0,0,0.3),
                ${DESIGN_TOKENS.shadows.button}
              `,
            }}
            data-testid="play-online-btn"
          >
            <div className="flex items-center gap-4">
              <Globe size={28} color={PALETTE.cream} />
              <div className="text-left">
                <span 
                  className="block"
                  style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontWeight: 700,
                    fontSize: '17px',
                    color: PALETTE.cream,
                    letterSpacing: '1px',
                  }}
                >
                  PLAY ONLINE
                </span>
                <span 
                  style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '12px',
                    color: `${PALETTE.cream}cc`,
                  }}
                >
                  Match up with players worldwide
                </span>
              </div>
            </div>
          </button>
        </RetroPanel>

        {/* Action Buttons */}
        <div className="space-y-3 mb-6">
          <Link to="/how-to-play" onClick={playClick} className="block">
            <RetroButton
              variant="secondary"
              size="md"
              fullWidth
              className="flex items-center justify-center gap-3"
              data-testid="how-to-play-btn"
            >
              <HelpCircle size={18} />
              HOW TO PLAY
            </RetroButton>
          </Link>

          <Link to="/account" onClick={playClick} className="block">
            <RetroButton
              variant="secondary"
              size="md"
              fullWidth
              className="flex items-center justify-center gap-3"
              data-testid="account-btn"
            >
              <Settings size={18} />
              ACCOUNT
            </RetroButton>
          </Link>

          {isAdmin && (
            <Link to="/admin" className="block">
              <RetroButton
                variant="tertiary"
                size="md"
                fullWidth
                className="flex items-center justify-center gap-3"
                data-testid="admin-btn"
              >
                <Shield size={18} />
                ADMIN DASHBOARD
              </RetroButton>
            </Link>
          )}
        </div>

        {/* Game History */}
        <RetroPanel title="GAME HISTORY" variant="secondary">
          {loading ? (
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '14px',
              color: PALETTE.ink,
              textAlign: 'center',
              padding: '32px 0',
            }}>
              Loading...
            </p>
          ) : games.length === 0 ? (
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '13px',
              color: `${PALETTE.ink}99`,
              textAlign: 'center',
              padding: '32px 0',
            }}>
              No games played yet. Start your first game!
            </p>
          ) : (
            <div className="space-y-4">
              {games.map((game) => (
                <div 
                  key={game.id} 
                  className="relative"
                  style={{
                    padding: '16px',
                    background: PALETTE.cream,
                    border: `${DESIGN_TOKENS.borders.medium} solid ${PALETTE.ink}`,
                    borderRadius: DESIGN_TOKENS.borderRadius.md,
                  }}
                  data-testid={`game-history-item-${game.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {/* Score Display */}
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <span style={{
                          fontFamily: DESIGN_TOKENS.fonts.ui,
                          fontWeight: 600,
                          fontSize: '14px',
                          color: PALETTE.ink,
                        }}>
                          {game.team_a.name}
                        </span>
                        <ScoreboardDisplay size="sm">
                          {game.team_a.score}
                        </ScoreboardDisplay>
                        <span style={{
                          fontFamily: DESIGN_TOKENS.fonts.ui,
                          fontSize: '12px',
                          color: PALETTE.dirt,
                        }}>
                          vs
                        </span>
                        <ScoreboardDisplay size="sm">
                          {game.team_b.score}
                        </ScoreboardDisplay>
                        <span style={{
                          fontFamily: DESIGN_TOKENS.fonts.ui,
                          fontWeight: 600,
                          fontSize: '14px',
                          color: PALETTE.ink,
                        }}>
                          {game.team_b.name}
                        </span>
                      </div>
                      
                      {/* Meta Info */}
                      <div className="flex items-center gap-4 flex-wrap">
                        <span style={{
                          fontFamily: DESIGN_TOKENS.fonts.ui,
                          fontSize: '11px',
                          color: PALETTE.dirt,
                        }}>
                          {formatDate(game.created_at)}
                        </span>
                        <span style={{
                          fontFamily: DESIGN_TOKENS.fonts.ui,
                          fontSize: '11px',
                          color: PALETTE.dirt,
                        }}>
                          {game.innings_played} innings
                        </span>
                        <span style={{
                          fontFamily: DESIGN_TOKENS.fonts.ui,
                          fontWeight: 600,
                          fontSize: '12px',
                          color: PALETTE.grass,
                        }}>
                          Winner: {game.winner}
                        </span>
                      </div>
                    </div>
                    
                    <RetroButton
                      variant="danger"
                      size="xs"
                      onClick={() => handleDeleteGame(game.id)}
                      data-testid={`delete-game-${game.id}`}
                    >
                      <Trash2 size={14} />
                    </RetroButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </RetroPanel>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '12px',
            color: PALETTE.grass,
          }}>
            Powered by Apple Music Previews
          </p>
        </div>
      </div>
    </RetroBackground>
  );
}
